import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SendIcon from '@mui/icons-material/Send';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { FC } from 'react';
import { useContext } from 'react';

import { useResponsive } from 'contexts/ResponsiveContext';
import { TicketsContext } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { CloseIcon, SearchIcon } from 'icons';

import { TicketsFiltersBar } from '../TicketsFiltersBar/TicketsFiltersBar';

import {
  BtnFlexContainerStyled,
  ClearBtnIconStyled,
  ClearBtnWrapStyled,
  FilterChipsRowStyled,
  FlexContainerStyled,
  HeaderStyled,
  InputStyled,
  MobileHeaderRowStyled,
  SearchAndFiltersRowStyled,
  SearchBarWrapStyled,
} from './Header.styled';
import useHeader from './hooks/useHeader';

type HeaderPropsT = {
  isCardsRowExpanded?: boolean;
  onToggleCardsRow?: () => void;
};

const Header: FC<HeaderPropsT> = ({ isCardsRowExpanded = true, onToggleCardsRow }) => {
  const {
    onChangeInput,
    searchText,
    openNewTicketPopup,
    openROListPopup,
    resetSearchTerm,
    handleExportClick,
    isExportingTickets,
  } = useHeader();
  const { isMobile } = useResponsive();
  const { activeTab, selectedRoofTop } = useContext(TicketsContext);
  const { setColumnPickerPopupVisibility } = useUserProfile();

  const searchBar = (
    <SearchBarWrapStyled title="Search for RO, Tech, Advisor...">
      <InputStyled
        id="searchTerm"
        name="searchTerm"
        icon={<SearchIcon />}
        placeholder="Search for RO, Tech, Advisor..."
        value={searchText}
        onChange={onChangeInput}
        bg="grey"
      />
      {searchText && (
        <ClearBtnWrapStyled justify="center">
          <ClearBtnIconStyled
            size="md"
            variant="transparent"
            icon={<CloseIcon size="xs" />}
            onClick={resetSearchTerm}
          />
        </ClearBtnWrapStyled>
      )}
    </SearchBarWrapStyled>
  );

  const actionButtons = (
    <BtnFlexContainerStyled>
      <Tooltip title={selectedRoofTop ? "Send RO's to WarrCloud" : 'Select a dealer first'} arrow>
        <span>
          <IconButton
            aria-label="Send RO's to WarrCloud"
            disabled={!selectedRoofTop}
            onClick={openROListPopup}
            sx={{ color: (theme): string => theme.palette.primary.main }}
          >
            <SendIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Export" arrow>
        <span>
          <IconButton
            aria-label="Export"
            onClick={handleExportClick}
            disabled={isExportingTickets}
            sx={{ color: (theme): string => theme.palette.primary.main }}
          >
            <FileDownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Customize table columns" arrow>
        <IconButton
          aria-label="Customize columns"
          onClick={(): void => setColumnPickerPopupVisibility(true)}
          sx={{ color: (theme): string => theme.palette.primary.main }}
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      {activeTab === ActiveTabTypeE.Tickets && (
        <Tooltip title="New ticket" arrow>
          <IconButton
            aria-label="New Ticket"
            onClick={openNewTicketPopup}
            className="open-ticket-btn"
            sx={{ color: (theme): string => theme.palette.primary.main }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      )}
      {onToggleCardsRow != null && (
        <Tooltip title={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'} arrow>
          <IconButton
            aria-label={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
            onClick={onToggleCardsRow}
            sx={{ color: (theme): string => theme.palette.primary.main }}
          >
            {isCardsRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      )}
    </BtnFlexContainerStyled>
  );

  if (isMobile) {
    return (
      <HeaderStyled>
        <TicketsFiltersBar
          renderMobileLayout={({ filterButtons, filterChips }): JSX.Element => (
            <>
              <MobileHeaderRowStyled>
                {searchBar}
                {filterButtons}
                {actionButtons}
              </MobileHeaderRowStyled>
              <FilterChipsRowStyled>{filterChips}</FilterChipsRowStyled>
            </>
          )}
        />
        <Divider />
      </HeaderStyled>
    );
  }

  return (
    <HeaderStyled>
      <FlexContainerStyled>
        <SearchAndFiltersRowStyled>
          {searchBar}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <TicketsFiltersBar />
          </Box>
        </SearchAndFiltersRowStyled>
        {actionButtons}
      </FlexContainerStyled>
      <Divider />
    </HeaderStyled>
  );
};

export default Header;
